import { Typography } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { CreateSecretForm } from '@/console/features/vaults/components/create-secret/create-secret-form';
import { TCreateSecretInputSchema } from '@/console/features/vaults/components/create-secret/create-secret.types';
import { createSecretInputSchema } from '@/console/features/vaults/components/create-secret/create-secret.validation';
import { TCreateSecretDialogProps } from '@/console/features/vaults/components/create-secret-dialog/create-secret-dialog.types';
import { LockIcon } from '@/shared/assets/icons/lock-icon';
import { TextButton } from '@/shared/components/ui/button/text-button/text-button';
import { Form } from '@/shared/components/ui/form/form';
import { TSubmittedData } from '@/shared/components/ui/form/form.types';
import { ModalController } from '@/shared/components/ui/modal-controller/modal-controller';
import { ModalContainer } from '@/shared/components/ui/modal-controller/modal-controller.styled';
import { useCreateSecretMutation } from '@/shared/redux/api/assets/vaults/vaults-api';
import { SecretTypeEnum } from '@/shared/types/types.generated';
import { showToastError } from '@/shared/utils/toast';

export const CreateSecretDialog: React.FC<TCreateSecretDialogProps> = ({
  vaultId,
  acceptedSecretTypes,
  onCreated,
}) => {
  const { t } = useTranslation();
  const [createSecret] = useCreateSecretMutation();
  const defaultSecretType =
    acceptedSecretTypes?.[0] ?? SecretTypeEnum.Credential;

  const onSubmit = async (
    data: TSubmittedData<TCreateSecretInputSchema>,
    handleClose: () => void,
  ) => {
    try {
      const response = await createSecret({
        secret: { ...data.data, vaultId },
      }).unwrap();

      toast.success(t('vault:secrets.form.create_success'));
      onCreated?.(response.createSecret);
      handleClose();
    } catch {
      showToastError(t('vault:secrets.form.create_error'));
    }
  };

  return (
    <ModalController
      trigger={
        <TextButton icon={<LockIcon />} variant="primaryUnderlined">
          {t('vault:secrets.add_secret')}
        </TextButton>
      }
    >
      {(handleClose) => (
        <ModalContainer gap="1.5rem" width="440px">
          <Typography variant="h5">{t('vault:secrets.add_secret')}</Typography>
          <Form<TCreateSecretInputSchema>
            onSubmit={(data) => onSubmit(data, handleClose)}
            schema={createSecretInputSchema}
            defaultValues={{ secretType: defaultSecretType }}
          >
            {() => (
              <CreateSecretForm
                handleClose={handleClose}
                acceptedSecretTypes={acceptedSecretTypes}
              />
            )}
          </Form>
        </ModalContainer>
      )}
    </ModalController>
  );
};
